<template>
  <b-modal v-model="localVisible" title="Value rules" size="lg" @ok="onSave" @hide="onHide">
    <template #title>
      Value rules
      <modal-help topic="value-rules" />
    </template>
    <p class="vr-intro">Rules run from top to bottom on every save. The first match sets this column. Always replaces the value each time. Only when replaces it when the conditions match, and otherwise leaves what was sent.</p>
    <div v-if="localRules.length" class="vr-list">
      <div v-for="(rule, index) in localRules" :key="rule.id" class="vr-rule">
        <button type="button" class="vr-open" @click="editRule(index)">
          <span class="vr-type">{{ rule.mode === 'onlyWhen' ? 'Only when' : 'Always' }}</span>
          <span class="vr-summary">{{ ruleSummary(rule) }}</span>
        </button>
        <button type="button" class="vr-remove" aria-label="Remove rule" @click="removeRule(index)">&times;</button>
      </div>
    </div>
    <div v-else-if="editingIndex === null" class="vr-empty">No value rules yet. A save keeps the value that was sent, then a default fills an empty value on create.</div>
    <div v-if="editingIndex !== null" class="vr-edit">
      <div class="vr-edit-title">{{ editingIndex === localRules.length ? 'New rule' : 'Edit rule' }}</div>
      <label class="vr-label">When this runs</label>
      <select v-model="editMode" class="vr-select">
        <option value="always">Always, on every save</option>
        <option value="onlyWhen">Only when the conditions match</option>
      </select>
      <template v-if="editMode === 'onlyWhen'">
        <div class="vr-conditions-head">
          <span>Conditions</span>
          <span v-if="editConditions.length > 1" class="vr-match">
            <button type="button" :class="{ active: editMatchMode === 'all' }" @click="editMatchMode = 'all'">All</button>
            <button type="button" :class="{ active: editMatchMode === 'any' }" @click="editMatchMode = 'any'">Any</button>
          </span>
        </div>
        <div v-for="(condition, index) in editConditions" :key="index" class="vr-condition">
          <select v-model="condition.source" class="vr-select" @change="onSourceChange(condition)">
            <option value="self">This column</option>
            <option value="field">Another column</option>
          </select>
          <select v-if="condition.source === 'field'" v-model="condition.field" class="vr-select" @change="onFieldChange(condition)">
            <option value="">Select column</option>
            <option v-for="column in otherColumns" :key="column.uniqueTitle" :value="column.uniqueTitle">{{ column.title || column.uniqueTitle }}</option>
          </select>
          <select v-model="condition.operator" class="vr-select">
            <option v-for="operator in operatorsFor(condition)" :key="operator.key" :value="operator.key">{{ operator.label }}</option>
          </select>
          <template v-if="needsValue(condition.operator)">
            <select v-model="condition.valueSource" class="vr-select">
              <option value="literal">A value</option>
              <option value="field">Another column</option>
            </select>
            <select v-if="condition.valueSource === 'field'" v-model="condition.valueField" class="vr-select">
              <option value="">Select column</option>
              <option v-for="column in otherColumns" :key="'cv-' + column.uniqueTitle" :value="column.uniqueTitle">{{ column.title || column.uniqueTitle }}</option>
            </select>
            <input v-else v-model="condition.value" class="vr-input" :placeholder="condition.operator === 'matches' || condition.operator === 'notMatches' ? 'pattern' : 'value'">
          </template>
          <button type="button" class="vr-remove" aria-label="Remove condition" @click="editConditions.splice(index, 1)">&times;</button>
        </div>
        <button type="button" class="vr-add" @click="addCondition">Add condition</button>
      </template>
      <label class="vr-label">Set this column from</label>
      <select v-model="editValueSource" class="vr-select">
        <option value="static">A value I type</option>
        <option value="field">Another column</option>
        <option value="related">A field on a related record</option>
      </select>
      <input v-if="editValueSource === 'static'" v-model="editStaticValue" class="vr-input" placeholder="value">
      <select v-if="editValueSource === 'field'" v-model="editField" class="vr-select">
        <option value="">Select column</option>
        <option v-for="column in otherColumns" :key="'set-' + column.uniqueTitle" :value="column.uniqueTitle">{{ column.title || column.uniqueTitle }}</option>
      </select>
      <template v-if="editValueSource === 'related'">
        <select v-model="editRelatedChoice" class="vr-select">
          <option value="">Select a relationship</option>
          <option v-for="choice in relatedChoices" :key="choice.id" :value="choice.id">{{ choice.label }}</option>
        </select>
        <select v-if="selectedRelated" v-model="editRelatedField" class="vr-select">
          <option value="">Select a field</option>
          <option v-for="field in selectedRelated.fields" :key="field.uniqueTitle" :value="field.uniqueTitle">{{ field.title || field.uniqueTitle }}</option>
        </select>
      </template>
      <div class="vr-edit-actions">
        <button type="button" class="btn btn-primary btn-sm" @click="saveEdit">Save rule</button>
        <button type="button" class="btn btn-secondary btn-sm" @click="cancelEdit">Cancel</button>
      </div>
    </div>
    <button v-if="editingIndex === null" type="button" class="vr-add" @click="startRule">Add rule</button>
  </b-modal>
</template>

<script>
import { dataTypeForColumn, isBlankOperator, validationOperators } from '../../../../shared/column-validation.js'
import { normalizeValueRules } from '../../../../shared/column-value-rules.js'

let nextRuleId = 1
const SCALAR = new Set(['text', 'number', 'dateTime', 'richText', 'multiselect', 'address'])

function blankCondition (dataType) {

  return {
    source: 'self',
    field: '',
    operator: 'empty',
    dataType,
    valueSource: 'literal',
    valueField: '',
    value: ''
  }
}

export default {
  props: {
    visible: { type: Boolean, default: false },
    rules: { type: Array, default: () => [] },
    column: { type: Object, default: () => ({}) },
    columns: { type: Array, default: () => [] },
    spreadsheets: { type: Array, default: () => [] },
    spreadsheetUniqueTitle: { type: String, default: '' }
  },
  data () {

    return {
      localVisible: false,
      localRules: [],
      editingIndex: null,
      editMode: 'always',
      editMatchMode: 'all',
      editConditions: [],
      editValueSource: 'static',
      editStaticValue: '',
      editField: '',
      editRelatedChoice: '',
      editRelatedField: ''
    }
  },
  computed: {
    columnDataType () {

      return dataTypeForColumn(this.column)
    },
    otherColumns () {

      const own = this.column && this.column.uniqueTitle

      return (this.columns || []).filter((column) => column && column.uniqueTitle && column.uniqueTitle !== own && SCALAR.has(column.type))
    },
    relatedChoices () {

      const choices = []
      const current = this.spreadsheetUniqueTitle

      for (const column of this.columns || []) {

        if (!column || column.type !== 'relationship' || !column.relatedSpreadSheet) continue

        const sheet = (this.spreadsheets || []).find((item) => item.uniqueTitle === column.relatedSpreadSheet)

        choices.push({
          id: `forward:${column.uniqueTitle}`,
          label: `${column.title || column.uniqueTitle} → ${sheet ? sheet.title : column.relatedSpreadSheet}`,
          direction: 'forward',
          relationshipColumn: column.uniqueTitle,
          fields: this.scalarFields(sheet)
        })
      }

      for (const sheet of this.spreadsheets || []) {

        if (!sheet || sheet.uniqueTitle === current) continue

        for (const column of sheet.columns || []) {

          if (!column || column.type !== 'relationship' || column.relatedSpreadSheet !== current) continue

          choices.push({
            id: `reverse:${sheet.uniqueTitle}:${column.uniqueTitle}`,
            label: `${sheet.title || sheet.uniqueTitle} (${column.title || column.uniqueTitle}) → this record`,
            direction: 'reverse',
            sourceSpreadsheet: sheet.uniqueTitle,
            sourceRelationshipColumn: column.uniqueTitle,
            fields: this.scalarFields(sheet)
          })
        }
      }

      return choices
    },
    selectedRelated () {

      return this.relatedChoices.find((choice) => choice.id === this.editRelatedChoice) || null
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler (value) {

        this.localVisible = value
        if (!value) return

        this.localRules = normalizeValueRules({ rules: this.rules }).rules.map((rule) => Object.assign({}, rule, {
          conditions: (rule.conditions || []).map((condition) => Object.assign({}, condition))
        }))
        this.cancelEdit()
      }
    }
  },
  methods: {
    scalarFields (sheet) {

      return ((sheet && sheet.columns) || []).filter((column) => column && SCALAR.has(column.type))
    },
    ruleSummary (rule) {

      const when = rule.mode === 'onlyWhen' ? 'when the conditions match' : 'on every save'
      let from = 'a typed value'

      if (rule.valueSource === 'field') from = rule.field || 'another column'
      if (rule.valueSource === 'related') from = rule.relatedField || 'a related field'

      return `Set ${when} from ${from}`
    },
    operatorsFor (condition) {

      const column = condition.source === 'field'
        ? this.otherColumns.find((item) => item.uniqueTitle === condition.field)
        : this.column

      return validationOperators(dataTypeForColumn(column || this.column)).filter((operator) => operator.key !== 'taken')
    },
    needsValue (operator) {

      return !isBlankOperator(operator)
    },
    onSourceChange (condition) {

      condition.field = ''
      condition.dataType = condition.source === 'self' ? this.columnDataType : ''
      condition.operator = this.operatorsFor(condition)[0].key
    },
    onFieldChange (condition) {

      const column = this.otherColumns.find((item) => item.uniqueTitle === condition.field)

      condition.dataType = dataTypeForColumn(column)
      condition.operator = this.operatorsFor(condition)[0].key
    },
    addCondition () {

      this.editConditions.push(blankCondition(this.columnDataType))
    },
    relatedChoiceId (rule) {

      if (!rule || rule.valueSource !== 'related') return ''
      if (rule.direction === 'reverse') return `reverse:${rule.sourceSpreadsheet}:${rule.sourceRelationshipColumn}`

      return `forward:${rule.relationshipColumn}`
    },
    startRule () {

      this.editingIndex = this.localRules.length
      this.editMode = 'always'
      this.editMatchMode = 'all'
      this.editConditions = [blankCondition(this.columnDataType)]
      this.editValueSource = 'static'
      this.editStaticValue = ''
      this.editField = ''
      this.editRelatedChoice = ''
      this.editRelatedField = ''
    },
    editRule (index) {

      const rule = this.localRules[index]

      this.editingIndex = index
      this.editMode = rule.mode === 'onlyWhen' ? 'onlyWhen' : 'always'
      this.editMatchMode = rule.conditionMatchMode || 'all'
      this.editConditions = (rule.conditions || []).map((condition) => Object.assign({}, condition))
      this.editValueSource = rule.valueSource || 'static'
      this.editStaticValue = rule.staticValue == null ? '' : String(rule.staticValue)
      this.editField = rule.field || ''
      this.editRelatedChoice = this.relatedChoiceId(rule)
      this.editRelatedField = rule.relatedField || ''
    },
    cancelEdit () {

      this.editingIndex = null
    },
    saveEdit () {

      const related = this.selectedRelated
      const rule = {
        id: this.editingIndex < this.localRules.length && this.localRules[this.editingIndex]
          ? this.localRules[this.editingIndex].id
          : `value_${Date.now()}_${nextRuleId++}`,
        mode: this.editMode,
        conditionMatchMode: this.editMatchMode,
        conditions: this.editMode === 'onlyWhen' ? this.editConditions.map((condition) => Object.assign({}, condition)) : [],
        valueSource: this.editValueSource,
        staticValue: this.editValueSource === 'static' ? this.editStaticValue : '',
        field: this.editValueSource === 'field' ? this.editField : '',
        direction: related ? related.direction : 'forward',
        relationshipColumn: related && related.direction === 'forward' ? related.relationshipColumn : '',
        sourceSpreadsheet: related && related.direction === 'reverse' ? related.sourceSpreadsheet : '',
        sourceRelationshipColumn: related && related.direction === 'reverse' ? related.sourceRelationshipColumn : '',
        relatedField: this.editValueSource === 'related' ? this.editRelatedField : ''
      }

      if (this.editingIndex >= this.localRules.length) this.localRules.push(rule)
      else this.localRules.splice(this.editingIndex, 1, rule)

      this.cancelEdit()
    },
    removeRule (index) {

      this.localRules.splice(index, 1)
      if (this.editingIndex === index) this.cancelEdit()
    },
    onSave () {

      if (this.editingIndex !== null) this.saveEdit()

      this.$emit('save', normalizeValueRules({ rules: this.localRules }).rules)
    },
    onHide () {

      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
.vr-intro, .vr-empty { color: #8a8a88; font-size: 0.85rem; }
.vr-list { display: flex; flex-direction: column; gap: 8px; margin: 12px 0; }
.vr-rule, .vr-condition, .vr-edit-actions, .vr-conditions-head, .vr-match { display: flex; align-items: center; gap: 8px; }
.vr-open { flex: 1; display: flex; align-items: center; gap: 8px; text-align: left; background: #1c1c1a; color: #e8e8e6; border: 1px solid #3a3a37; border-radius: 6px; padding: 8px 10px; }
.vr-type { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; color: #6cb6ff; }
.vr-summary { flex: 1; }
.vr-remove { border: 0; background: transparent; color: #f82471; font-size: 1.1rem; }
.vr-edit { border: 1px solid #3a3a37; border-radius: 6px; padding: 12px; margin-top: 12px; }
.vr-edit-title, .vr-label { display: block; margin: 8px 0 4px; color: #b0b0ae; font-size: 0.85rem; }
.vr-select, .vr-input { background: #141412; color: #e8e8e6; border: 1px solid #3a3a37; border-radius: 4px; padding: 4px 8px; }
.vr-condition { margin-top: 8px; flex-wrap: wrap; }
.vr-add { margin-top: 8px; border: 0; background: transparent; color: #6cb6ff; padding: 0; }
.vr-match button { border: 1px solid #3a3a37; background: transparent; color: #b0b0ae; border-radius: 4px; padding: 2px 8px; }
.vr-match button.active { color: #e8e8e6; border-color: #6cb6ff; }
</style>
